import { useEffect, useMemo, useState } from 'react'
import { Loading } from 'react-daisyui'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useDriveViewContext, DriveViewAlbum } from '@/contexts/driveViewContext.tsx'
import { useDriveViewFilesContext } from '@/contexts/driveViewFilesContext.tsx'
import DriveHeader from '@/components/DriveHeader.tsx'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import ScrollDownIndicator from '@/components/ScrollDownIndicator.tsx'
import FilesListing, { LayoutMode } from '@/components/Files/FilesListing.tsx'
import FilesSlideshow from '@/components/Files/FilesSlideshow.tsx'
import FilesFilter from '@/components/FilesFilter.tsx'
import ContactFooter from '@/components/ContactFooter.tsx'
import AccessExpiredElement from '@/components/AccessExpiredElement.tsx'
import { AlbumFile } from '@/models/albumFile.ts'
import { getScrollIndicatorConfig, isDriveAccessExpired, OpenedImageCaption } from '@/models/drive.ts'
import { DownloadOptions } from '@/models/downloadOptions.ts'

export default function DriveViewer() {
  const { metadata, albums, currentAlbum, isLoading, error, onSelectAlbum, fetchAlbumFiles } = useDriveViewContext()

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-error">Fehler beim Laden</p>
      </div>
    )
  }

  if (!metadata) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>Nicht gefunden</p>
      </div>
    )
  }

  const theme = metadata.theme
  const hasThemedHeader = !!theme?.header_style

  // When user access has expired, the backend stops returning album content early.
  // Show a hint to contact the photographer instead of the (empty) content and albums.
  if (isDriveAccessExpired(metadata.userDefinedExpiresAt)) {
    return (
      <div>
        <DriveHeader
          shareMode={{
            driveTitle: metadata.driveTitle,
            driveType: metadata.driveType,
            title: metadata.title,
            albumThumbnail: currentAlbum?.thumbnail,
            albumDescription: currentAlbum?.description,
            headerStyle: theme?.header_style ?? null,
            previewDesktopUrl: currentAlbum?.preview_desktop?.detail ?? currentAlbum?.preview_desktop?.file ?? currentAlbum?.thumbnail,
            previewMobileUrl: currentAlbum?.preview_mobile?.detail ?? currentAlbum?.preview_mobile?.file ?? currentAlbum?.thumbnail,
            previewDesktopCenterX: currentAlbum?.preview_desktop_center_x,
            previewDesktopCenterY: currentAlbum?.preview_desktop_center_y,
            previewMobileCenterX: currentAlbum?.preview_mobile_center_x,
            previewMobileCenterY: currentAlbum?.preview_mobile_center_y,
            contact: metadata.contact,
          }}
        />
        <AccessExpiredElement />
        {metadata.contact && <ContactFooter contact={metadata.contact} />}
      </div>
    )
  }

  // Resolve effective values: API override → theme default → component default
  const effectiveSpacing = metadata.spacing ?? theme?.spacing ?? undefined
  const effectiveLayoutMode = metadata.layoutMode ?? theme?.layout_mode ?? undefined
  const effectiveAlbumOverview = metadata.albumOverview ?? theme?.album_overview ?? undefined
  const effectiveAlbumOverviewStyle = metadata.albumOverviewStyle ?? theme?.album_overview_style ?? undefined
  const effectiveContained = metadata.areElementsContained ?? theme?.are_elements_contained ?? undefined
  const containerClass = effectiveContained === false ? 'w-full' : 'container mx-auto'

  function handleScrollToAlbum(album: DriveViewAlbum) {
    const element = document.getElementById(`album-${album.id}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div>
      <div className='relative'>
        <DriveHeader
          shareMode={{
            driveTitle: metadata.driveTitle,
            driveType: metadata.driveType,
            title: metadata.title,
            albumThumbnail: currentAlbum?.thumbnail,
            albumDescription: currentAlbum?.description,
            headerStyle: theme?.header_style ?? null,
            previewDesktopUrl: currentAlbum?.preview_desktop?.detail ?? currentAlbum?.preview_desktop?.file ?? currentAlbum?.thumbnail,
            previewMobileUrl: currentAlbum?.preview_mobile?.detail ?? currentAlbum?.preview_mobile?.file ?? currentAlbum?.thumbnail,
            previewDesktopCenterX: currentAlbum?.preview_desktop_center_x,
            previewDesktopCenterY: currentAlbum?.preview_desktop_center_y,
            previewMobileCenterX: currentAlbum?.preview_mobile_center_x,
            previewMobileCenterY: currentAlbum?.preview_mobile_center_y,
            contact: metadata.contact,
          }}
        />
        {hasThemedHeader && (
          <ScrollDownIndicator
            targetId='drive-viewer-content'
            className='absolute bottom-4 left-0 right-0 z-20'
            indicatorConfig={getScrollIndicatorConfig(theme?.header_style)}
          />
        )}
      </div>
      <div id='drive-viewer-content'>
        {/* Album overview */}
        {albums.length > 1 && (
          <AlbumSelection
            externalAlbums={albums}
            selectedAlbum={hasThemedHeader ? null : currentAlbum}
            setSelectedAlbum={onSelectAlbum}
            useDirectAlbumData
            overviewStyle={effectiveAlbumOverviewStyle ?? undefined}
            overviewVisibility={effectiveAlbumOverview ?? undefined}
            onScrollToAlbum={hasThemedHeader ? handleScrollToAlbum : undefined}
          />
        )}

        {/* Themed view: all albums stacked */}
        {hasThemedHeader ? (
          <div className={`${containerClass} px-4`}>
            {albums.map((album, index) => (
              <DriveViewerAlbumSection
                key={album.id}
                album={album}
                fetchAlbumFiles={fetchAlbumFiles}
                driveType={metadata.driveType}
                openedImageCaption={metadata.openedImageCaption}
                downloadOptions={metadata.downloadOptions}
                layoutMode={effectiveLayoutMode}
                spacing={effectiveSpacing}
                isFirst={index === 0}
              />
            ))}
          </div>
        ) : (
          /* Normal view: single album */
          currentAlbum && (
            <DriveViewerSingleAlbum
              driveType={metadata.driveType}
              openedImageCaption={metadata.openedImageCaption}
              downloadOptions={metadata.downloadOptions}
              containerClass={containerClass}
              layoutMode={effectiveLayoutMode}
              spacing={effectiveSpacing}
            />
          )
        )}
      </div>
      {metadata.contact && (
        <ContactFooter contact={metadata.contact} />
      )}
    </div>
  )
}

// Single album view (non-themed) — uses the files context provider
interface DriveViewerSingleAlbumProps {
  driveType: string
  openedImageCaption?: OpenedImageCaption | null
  downloadOptions?: DownloadOptions | null
  containerClass: string
  layoutMode?: LayoutMode
  spacing?: number
}

function DriveViewerSingleAlbum({ driveType, openedImageCaption, downloadOptions, containerClass, layoutMode, spacing }: DriveViewerSingleAlbumProps) {
  const filesContext = useDriveViewFilesContext()
  const { ref, inView } = useInView()
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)

  const currentFilesCollection = filesContext?.currentFilesCollection ?? null
  const hasNextPage = filesContext?.hasNextPage
  const fetchNextPage = filesContext?.fetchNextPage
  const filesLoading = filesContext?.isLoading ?? false

  const filesFilter = FilesFilter({ isLoading: filesLoading })

  useEffect(() => {
    if (hasNextPage && fetchNextPage && inView) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  function onFileClick(file: AlbumFile) {
    const indexOfFile = currentFilesCollection?.files.findIndex(f => f.id === file.id) ?? -1
    if (indexOfFile !== -1) {
      setSelectedFileIndex(indexOfFile)
    }
  }

  return (
    <div className={`${containerClass} flex flex-col gap-3 justify-center`}>
      <div className="flex flex-row gap-3 px-2">
        <div className="flex-1" />
        {filesFilter.button}
      </div>
      {filesFilter.panel}
      {filesLoading && (
        <div className="flex justify-center py-8">
          <Loading color="primary" />
        </div>
      )}
      {!filesLoading && currentFilesCollection && (
        <FilesListing
          files={currentFilesCollection}
          onFileClick={onFileClick}
          layoutMode={layoutMode}
          spacing={spacing}
        />
      )}
      {currentFilesCollection && (
        <FilesSlideshow
          files={currentFilesCollection.files}
          selectedFileIndex={selectedFileIndex}
          onSelectFileIndex={setSelectedFileIndex}
          shareMode={true}
          showAuthorInfo={driveType !== 'prof'}
          openedImageCaption={openedImageCaption}
          downloadOptions={downloadOptions}
        />
      )}
      <div className="w-1 h-1" ref={ref} />
    </div>
  )
}

// Album section for themed all-albums view — independently fetches its own files
interface DriveViewerAlbumSectionProps {
  album: DriveViewAlbum
  fetchAlbumFiles: (albumId: string) => Promise<any>
  driveType: string
  openedImageCaption?: OpenedImageCaption | null
  downloadOptions?: DownloadOptions | null
  layoutMode?: LayoutMode
  spacing?: number
  isFirst: boolean
}

function DriveViewerAlbumSection({ album, fetchAlbumFiles, driveType, openedImageCaption, downloadOptions, layoutMode, spacing, isFirst }: DriveViewerAlbumSectionProps) {
  const { ref, inView } = useInView()
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)

  const query = useInfiniteQuery({
    queryKey: ['driveViewer', 'album', album.id, 'files'],
    initialPageParam: null as string | null,
    async queryFn({ pageParam }) {
      if (pageParam) {
        const response = await fetch(pageParam)
        return response.json()
      }
      return fetchAlbumFiles(album.id)
    },
    getNextPageParam(page: any) {
      return page.next
    },
  })

  useEffect(() => {
    if (inView && query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }, [inView, query.hasNextPage, query.isFetchingNextPage])

  const files = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.flatMap((page: any) => page.results || page) as AlbumFile[]
  }, [query.data])

  function handleFileClick(file: AlbumFile) {
    const index = files.findIndex(f => f.id === file.id)
    if (index !== -1) {
      setSelectedFileIndex(index)
    }
  }

  return (
    <section id={`album-${album.id}`} className={isFirst ? 'pt-8' : 'pt-16'}>
      <h2 className='font-serif text-3xl text-center mb-8 text-content'>
        {album.title}
      </h2>

      {album.description && (
        <p className='text-center text-content opacity-75 mb-8 max-w-2xl mx-auto'>
          {album.description}
        </p>
      )}

      {files.length > 0 ? (
        <FilesListing
          files={{ files }}
          onFileClick={handleFileClick}
          layoutMode={layoutMode}
          spacing={spacing}
        />
      ) : (
        !query.isLoading && (
          <div className='text-center py-8 text-content opacity-50'>
            Keine Bilder in diesem Album vorhanden
          </div>
        )
      )}

      {query.isLoading && (
        <div className='flex justify-center py-8'>
          <Loading color="primary" />
        </div>
      )}

      <FilesSlideshow
        files={files}
        selectedFileIndex={selectedFileIndex}
        onSelectFileIndex={setSelectedFileIndex}
        shareMode={true}
        showAuthorInfo={driveType !== 'prof'}
        openedImageCaption={openedImageCaption}
        downloadOptions={downloadOptions}
      />

      <div ref={ref} className='h-1 w-1' />

      {query.isFetchingNextPage && (
        <div className='flex justify-center py-8'>
          <Loading color="primary" />
        </div>
      )}
    </section>
  )
}
